import React from 'react'
import styled from 'styled-components'

import { SkeletonBox } from 'features/charts-v2/components/skeletons/SkeletonBox'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'

const BarSkeleton: React.FC = () => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px',
        background: 'white',
      }}
    >
      <SkeletonBox
        width="30%"
        height="24px"
        style={{ minWidth: '120px', maxWidth: '250px', marginBottom: '8px' }}
      />
      <SkeletonBox
        width="15%"
        height="14px"
        style={{ minWidth: '80px', maxWidth: '150px', marginBottom: '16px' }}
      />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-evenly',
          paddingLeft: '120px',
        }}
      >
        {[55, 75, 35, 60, 45, 80, 30, 65].map((width, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              position: 'relative',
            }}
          >
            <SkeletonBox
              width="100px"
              height="16px"
              style={{
                position: 'absolute',
                left: '-110px',
                minWidth: '60px',
                maxWidth: '100px',
              }}
            />
            <SkeletonBox
              width={`${width}%`}
              height="24px"
              style={{ minWidth: '40px', maxWidth: '600px' }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

const RidgelineSkeletonFrame = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  background: white;
`

const RidgelineSkeletonTitle = styled(SkeletonBox)`
  min-width: 120px;
  max-width: 250px;
  margin-bottom: 8px;
`

const RidgelineSkeletonSubtitle = styled(SkeletonBox)`
  min-width: 80px;
  max-width: 150px;
  margin-bottom: 16px;
`

const RidgelineSkeletonRows = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  padding-left: 120px;
`

const RidgelineSkeletonRow = styled.div`
  display: flex;
  align-items: flex-end;
  width: 100%;
  position: relative;
`

const RidgelineSkeletonLabel = styled(SkeletonBox)`
  position: absolute;
  left: -110px;
  bottom: 0;
  min-width: 60px;
  max-width: 100px;
`

const RidgelineSkeletonHill = styled(SkeletonBox)<{ $offset: number }>`
  margin-left: ${(props) => props.$offset}%;
  border-radius: 50% 50% 2px 2px / 100% 100% 2px 2px;
  min-width: 40px;
  max-width: 400px;
`

const RidgelineSkeleton: React.FC = () => (
  <RidgelineSkeletonFrame>
    <RidgelineSkeletonTitle width="30%" height="24px" />
    <RidgelineSkeletonSubtitle width="15%" height="14px" />
    <RidgelineSkeletonRows>
      {[55, 70, 40, 62, 48, 75, 35, 58].map((offset, i) => (
        <RidgelineSkeletonRow key={i}>
          <RidgelineSkeletonLabel width="100px" height="16px" />
          <RidgelineSkeletonHill
            $offset={offset - 30}
            width="30%"
            height="28px"
          />
        </RidgelineSkeletonRow>
      ))}
    </RidgelineSkeletonRows>
  </RidgelineSkeletonFrame>
)

const SankeySkeletonFrame = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  background: white;
`

const SankeySkeletonTitle = styled(SkeletonBox)`
  min-width: 120px;
  max-width: 250px;
  margin-bottom: 8px;
`

const SankeySkeletonSubtitle = styled(SkeletonBox)`
  min-width: 80px;
  max-width: 150px;
  margin-bottom: 16px;
`

const SankeySkeletonColumns = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  justify-content: space-between;
`

const SankeySkeletonColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  width: 14px;
`

const SANKEY_SKELETON_COLUMNS: number[][] = [
  [38, 24, 18],
  [30, 30, 24],
  [26, 34, 20],
]

const SankeySkeleton: React.FC = () => (
  <SankeySkeletonFrame>
    <SankeySkeletonTitle width="30%" height="24px" />
    <SankeySkeletonSubtitle width="15%" height="14px" />
    <SankeySkeletonColumns>
      {SANKEY_SKELETON_COLUMNS.map((heights, columnIndex) => (
        <SankeySkeletonColumn key={columnIndex}>
          {heights.map((height, nodeIndex) => (
            <SkeletonBox key={nodeIndex} width="100%" height={`${height}%`} />
          ))}
        </SankeySkeletonColumn>
      ))}
    </SankeySkeletonColumns>
  </SankeySkeletonFrame>
)

const LineSkeleton: React.FC = () => {
  const points = [30, 45, 35, 60, 40, 55, 35, 50, 45]

  const smoothPathData = points
    .map((y, i) => {
      const x = (i / (points.length - 1)) * 100
      if (i === 0) return `M ${x} ${100 - y}`
      const prevX = ((i - 1) / (points.length - 1)) * 100
      const prevPoint = points[i - 1]
      if (prevPoint === undefined) return ''
      const prevY = 100 - prevPoint
      const cpX1 = prevX + (x - prevX) / 3
      const cpX2 = prevX + ((x - prevX) * 2) / 3
      return `C ${cpX1} ${prevY}, ${cpX2} ${100 - y}, ${x} ${100 - y}`
    })
    .join(' ')

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px',
        background: 'white',
      }}
    >
      <SkeletonBox
        width="30%"
        height="24px"
        style={{ minWidth: '120px', maxWidth: '250px', marginBottom: '8px' }}
      />
      <SkeletonBox
        width="15%"
        height="14px"
        style={{ minWidth: '80px', maxWidth: '150px', marginBottom: '16px' }}
      />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          padding: '24px 12px 32px 12px',
          position: 'relative',
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ overflow: 'visible' }}
        >
          <defs>
            <linearGradient id="lineShimmer" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#f0f0f0" />
              <stop offset="50%" stopColor="#e0e0e0" />
              <stop offset="100%" stopColor="#f0f0f0" />
              <animate
                attributeName="x1"
                values="-100%;100%"
                dur="1.5s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="x2"
                values="0%;200%"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </linearGradient>
          </defs>
          <path
            d={smoothPathData}
            fill="none"
            stroke="url(#lineShimmer)"
            strokeWidth="3"
            vectorEffect="non-scaling-stroke"
            style={{ opacity: 0.8 }}
          />
        </svg>
      </div>
    </div>
  )
}

const StackedBarSkeleton: React.FC = () => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px',
        background: 'white',
      }}
    >
      <SkeletonBox
        width="30%"
        height="24px"
        style={{ minWidth: '120px', maxWidth: '250px', marginBottom: '8px' }}
      />
      <SkeletonBox
        width="15%"
        height="14px"
        style={{ minWidth: '80px', maxWidth: '150px', marginBottom: '16px' }}
      />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-evenly',
          paddingLeft: '120px',
        }}
      >
        {[
          [25, 30, 20],
          [35, 25, 15],
          [20, 30, 25],
          [30, 35, 20],
          [25, 20, 30],
          [35, 30, 25],
          [20, 25, 35],
          [30, 25, 20],
        ].map((segments, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              position: 'relative',
            }}
          >
            <SkeletonBox
              width="100px"
              height="16px"
              style={{
                position: 'absolute',
                left: '-110px',
                minWidth: '60px',
                maxWidth: '100px',
              }}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '2px',
                height: '24px',
                width: '80%',
                maxWidth: '600px',
              }}
            >
              {segments.map((width, j) => (
                <SkeletonBox
                  key={j}
                  width={`${width}%`}
                  height="100%"
                  style={{ minWidth: '12px' }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const BubbleSkeleton: React.FC = () => {
  const bubbles = [
    { left: '15%', top: '20%', size: '48px' },
    { left: '35%', top: '45%', size: '64px' },
    { left: '55%', top: '25%', size: '40px' },
    { left: '25%', top: '65%', size: '56px' },
    { left: '70%', top: '55%', size: '72px' },
    { left: '45%', top: '75%', size: '44px' },
    { left: '80%', top: '35%', size: '52px' },
    { left: '60%', top: '80%', size: '60px' },
  ]

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px',
        background: 'white',
      }}
    >
      <SkeletonBox
        width="30%"
        height="24px"
        style={{ minWidth: '120px', maxWidth: '250px', marginBottom: '8px' }}
      />
      <SkeletonBox
        width="15%"
        height="14px"
        style={{ minWidth: '80px', maxWidth: '150px', marginBottom: '16px' }}
      />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          padding: '24px',
        }}
      >
        {bubbles.map((bubble, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: bubble.left,
              top: bubble.top,
              width: bubble.size,
              height: bubble.size,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <SkeletonBox
              width="100%"
              height="100%"
              style={{ borderRadius: '50%' }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

const ScatterSkeleton: React.FC = () => {
  const dots = [
    { left: '12%', top: '25%' },
    { left: '18%', top: '45%' },
    { left: '25%', top: '35%' },
    { left: '32%', top: '60%' },
    { left: '38%', top: '30%' },
    { left: '45%', top: '50%' },
    { left: '52%', top: '40%' },
    { left: '58%', top: '65%' },
    { left: '65%', top: '35%' },
    { left: '72%', top: '55%' },
    { left: '78%', top: '45%' },
    { left: '85%', top: '70%' },
  ]

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px',
        background: 'white',
      }}
    >
      <SkeletonBox
        width="30%"
        height="24px"
        style={{ minWidth: '120px', maxWidth: '250px', marginBottom: '8px' }}
      />
      <SkeletonBox
        width="15%"
        height="14px"
        style={{ minWidth: '80px', maxWidth: '150px', marginBottom: '16px' }}
      />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          padding: '24px',
        }}
      >
        {dots.map((dot, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: dot.left,
              top: dot.top,
              width: '12px',
              height: '12px',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <SkeletonBox
              width="100%"
              height="100%"
              style={{ borderRadius: '50%' }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

const PieSkeleton: React.FC = () => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px',
        background: 'white',
      }}
    >
      <SkeletonBox
        width="30%"
        height="24px"
        style={{ minWidth: '120px', maxWidth: '250px', marginBottom: '8px' }}
      />
      <SkeletonBox
        width="15%"
        height="14px"
        style={{ minWidth: '80px', maxWidth: '150px', marginBottom: '16px' }}
      />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div
          style={{
            width: '60%',
            maxWidth: '280px',
            aspectRatio: '1',
            position: 'relative',
          }}
        >
          <SkeletonBox
            width="100%"
            height="100%"
            style={{ borderRadius: '50%' }}
          />
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '50%',
              height: '50%',
              background: 'white',
              borderRadius: '50%',
            }}
          />
        </div>
      </div>
    </div>
  )
}

const HeatmapSkeleton: React.FC = () => {
  const rows = 8
  const cols = 10

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px',
        background: 'white',
      }}
    >
      <SkeletonBox
        width="30%"
        height="24px"
        style={{ minWidth: '120px', maxWidth: '250px', marginBottom: '8px' }}
      />
      <SkeletonBox
        width="15%"
        height="14px"
        style={{ minWidth: '80px', maxWidth: '150px', marginBottom: '16px' }}
      />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          padding: '8px 0',
        }}
      >
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <div
            key={rowIdx}
            style={{
              flex: 1,
              display: 'flex',
              gap: '4px',
              minHeight: 0,
            }}
          >
            {Array.from({ length: cols }).map((_, colIdx) => (
              <SkeletonBox
                key={colIdx}
                width={`${100 / cols}%`}
                height="100%"
                style={{ minWidth: '20px', minHeight: '20px' }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

const TreemapSkeleton: React.FC = () => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px',
        background: 'white',
      }}
    >
      <SkeletonBox
        width="30%"
        height="24px"
        style={{ minWidth: '120px', maxWidth: '250px', marginBottom: '8px' }}
      />
      <SkeletonBox
        width="15%"
        height="14px"
        style={{ minWidth: '80px', maxWidth: '150px', marginBottom: '16px' }}
      />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gridTemplateRows: '2fr 1fr',
          gap: '4px',
          padding: '8px 0',
        }}
      >
        <div style={{ gridRow: 'span 2', minHeight: 0 }}>
          <SkeletonBox width="100%" height="100%" />
        </div>
        <div style={{ minHeight: 0 }}>
          <SkeletonBox width="100%" height="100%" />
        </div>
        <div style={{ gridRow: 'span 2', minHeight: 0 }}>
          <SkeletonBox width="100%" height="100%" />
        </div>
        <div style={{ minHeight: 0 }}>
          <SkeletonBox width="100%" height="100%" />
        </div>
      </div>
    </div>
  )
}

const TableChartSkeleton: React.FC = () => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px',
        background: 'white',
      }}
    >
      <SkeletonBox
        width="30%"
        height="24px"
        style={{ minWidth: '120px', maxWidth: '250px', marginBottom: '8px' }}
      />
      <SkeletonBox
        width="15%"
        height="14px"
        style={{ minWidth: '80px', maxWidth: '150px', marginBottom: '16px' }}
      />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <SkeletonBox
            width="20%"
            height="16px"
            style={{ minWidth: '80px', maxWidth: '150px' }}
          />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '24px' }}>
            <SkeletonBox width="60px" height="16px" />
            <SkeletonBox width="80px" height="16px" />
            <SkeletonBox width="60px" height="16px" />
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {[25, 30, 22, 35, 24, 28, 32, 23, 26, 29].map((widthPercent, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid #f5f5f5',
              }}
            >
              <SkeletonBox
                width={`${widthPercent}%`}
                height="14px"
                style={{ minWidth: '80px', maxWidth: '200px' }}
              />
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '24px' }}>
                <SkeletonBox width="40px" height="14px" />
                <SkeletonBox width="50px" height="14px" />
                <SkeletonBox width="40px" height="14px" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

interface ChartSkeletonProps {
  chartType: ChartTypeV2
}

export const ChartSkeleton: React.FC<ChartSkeletonProps> = ({ chartType }) => {
  switch (chartType) {
    case ChartTypeV2.BAR_HORIZONTAL:
      return <BarSkeleton />
    case ChartTypeV2.RIDGELINE:
      return <RidgelineSkeleton />
    case ChartTypeV2.SANKEY:
      return <SankeySkeleton />
    case ChartTypeV2.LINE:
      return <LineSkeleton />
    case ChartTypeV2.BAR_HORIZONTAL_STACKED:
      return <StackedBarSkeleton />
    case ChartTypeV2.BUBBLE:
      return <BubbleSkeleton />
    case ChartTypeV2.SCATTER:
      return <ScatterSkeleton />
    case ChartTypeV2.PIE:
      return <PieSkeleton />
    case ChartTypeV2.HEATMAP:
      return <HeatmapSkeleton />
    case ChartTypeV2.TREEMAP:
      return <TreemapSkeleton />
    case ChartTypeV2.TABLE:
      return <TableChartSkeleton />
    default:
      return <BarSkeleton />
  }
}
